import React, { useEffect, useState } from 'react';

import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../ui/table';
import IconWrapper from '../../../ui/IconWrapper';
import { Timestamp } from '../../../ui/timestamp';
import { FiCopy } from 'react-icons/fi';
import {
  useScheduledJob,
  useScheduledJobRun,
  useScheduledJobRuns,
} from '../../../../hooks/useScheduledJobs';
import type { ScheduledJob } from '../../../../types/scheduledJobs';
import { formatCadence } from '../../../../types/scheduledJobs';
import { RunStatusIcon, getRunStatusBadgeVariant } from './statusIcon';

interface ScheduledJobDetailDrawerProps {
  jobId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SummaryRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
      {label}
    </span>
    <span className="text-right text-[13px] text-foreground">{value}</span>
  </div>
);

const RunDetailPane: React.FC<{
  jobId: string;
  runId: string | null;
}> = ({ jobId, runId }) => {
  const { data: run, isLoading } = useScheduledJobRun(jobId, runId);

  if (!runId) {
    return (
      <div className="flex h-full min-h-[240px] items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
        Select a run to view output
      </div>
    );
  }

  if (isLoading || !run) {
    return (
      <div className="flex h-full min-h-[240px] animate-pulse items-center justify-center rounded-md border bg-muted/50" />
    );
  }

  return (
    <div className="flex min-h-[240px] flex-col rounded-md border bg-muted/40">
      <div className="flex items-center justify-between gap-2 border-b bg-background px-3 py-2">
        <div className="flex items-center gap-2">
          <RunStatusIcon status={run.status} />
          <Badge variant={getRunStatusBadgeVariant(run.status)} className="capitalize">
            {run.status}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {run.started_at ? (
              <>
                Started <Timestamp value={run.started_at} format="relative" />
              </>
            ) : (
              'Not started'
            )}
          </span>
        </div>
        {(run.output || run.error) && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              const text = run.error ?? run.output ?? '';
              if (!text) return;
              navigator.clipboard?.writeText(text).catch(() => undefined);
            }}
          >
            <IconWrapper icon={FiCopy} className="mr-1.5 h-3.5 w-3.5" />
            Copy
          </Button>
        )}
      </div>
      <pre
        className="flex-1 overflow-auto whitespace-pre-wrap break-words p-3 font-mono text-[12px] leading-relaxed text-foreground"
        style={{ maxHeight: '360px' }}
      >
        {run.error
          ? run.error
          : run.output
            ? run.output
            : run.status === 'pending' || run.status === 'running'
              ? 'Run in progress…'
              : 'No output recorded.'}
      </pre>
    </div>
  );
};

const ScheduledJobDetailDrawer: React.FC<ScheduledJobDetailDrawerProps> = ({
  jobId,
  open,
  onOpenChange,
}) => {
  const { data: job, isLoading: jobLoading } = useScheduledJob(jobId);
  const { data: runsResponse, isLoading: runsLoading } = useScheduledJobRuns(
    jobId,
    25,
    0,
  );
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  // Auto-select the most recent run when the drawer first loads data.
  useEffect(() => {
    if (!open) {
      setSelectedRunId(null);
      return;
    }
    const runs = runsResponse?.items ?? [];
    if (runs.length > 0 && !selectedRunId) {
      setSelectedRunId(runs[0].id);
    }
  }, [open, runsResponse, selectedRunId]);

  const runs = runsResponse?.items ?? [];
  const currentJob: ScheduledJob | undefined = job;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[720px] lg:max-w-[840px]"
      >
        <SheetHeader className="p-6 pb-4">
          <SheetTitle className="text-xl">
            {currentJob?.name ?? 'Scheduled job'}
          </SheetTitle>
          <SheetDescription>
            {currentJob
              ? formatCadence(currentJob.cadence, currentJob.daily_time_utc)
              : 'Loading…'}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 pb-8">
          {jobLoading || !currentJob ? (
            <div className="h-24 animate-pulse rounded-md bg-muted" />
          ) : (
            <>
              <section className="rounded-lg border bg-card p-4">
                <SummaryRow
                  label="Status"
                  value={
                    currentJob.status === 'paused' ? (
                      <Badge variant="outline" className="border-amber-300 text-amber-700">
                        Paused
                      </Badge>
                    ) : (
                      <Badge variant="default">Active</Badge>
                    )
                  }
                />
                <SummaryRow
                  label="Next run"
                  value={
                    currentJob.status === 'paused' ? (
                      'Paused'
                    ) : (
                      <Timestamp value={currentJob.next_run_at} format="future" />
                    )
                  }
                />
                <SummaryRow
                  label="Last run"
                  value={<Timestamp value={currentJob.last_run_at} format="relative" />}
                />
                <SummaryRow
                  label="Created"
                  value={<Timestamp value={currentJob.created_at} format="absolute" />}
                />
              </section>

              <section>
                <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  Prompt
                </h3>
                <div className="rounded-lg border bg-muted/30 p-3">
                  <pre className="whitespace-pre-wrap break-words font-sans text-[13px] leading-relaxed text-foreground">
                    {currentJob.prompt}
                  </pre>
                </div>
              </section>

              <section>
                <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  Run history
                </h3>
                {runsLoading ? (
                  <div className="h-24 animate-pulse rounded-md border bg-muted/50" />
                ) : runs.length === 0 ? (
                  <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                    No runs yet.
                  </div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[28px]" />
                          <TableHead>Status</TableHead>
                          <TableHead>Started</TableHead>
                          <TableHead>Finished</TableHead>
                          <TableHead>Duration</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {runs.map((run) => {
                          const isSelected = run.id === selectedRunId;
                          const duration =
                            run.started_at && run.finished_at
                              ? Math.max(
                                  0,
                                  (new Date(run.finished_at).getTime() -
                                    new Date(run.started_at).getTime()) /
                                    1000,
                                )
                              : null;
                          return (
                            <TableRow
                              key={run.id}
                              data-state={isSelected ? 'selected' : undefined}
                              className="cursor-pointer"
                              onClick={() => setSelectedRunId(run.id)}
                            >
                              <TableCell className="pr-0">
                                <RunStatusIcon status={run.status} />
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={getRunStatusBadgeVariant(run.status)}
                                  className="capitalize"
                                >
                                  {run.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-[13px] text-muted-foreground">
                                <Timestamp value={run.started_at} format="relative" />
                              </TableCell>
                              <TableCell className="text-[13px] text-muted-foreground">
                                <Timestamp value={run.finished_at} format="relative" />
                              </TableCell>
                              <TableCell className="text-[13px] text-muted-foreground">
                                {duration != null ? `${duration.toFixed(1)}s` : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </section>

              <section>
                <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  Run output
                </h3>
                <RunDetailPane jobId={currentJob.id} runId={selectedRunId} />
              </section>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ScheduledJobDetailDrawer;
